// Chapter 3 - Selecting and Defining Target Behaviors
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 3.
// All content traced to source-pdfs/Chapter-3-Selecting-and-Defining-Target-Behaviors-2.pdf

import type { ChapterModule } from "./types";

const chapter03Module: ChapterModule = {
  meta: {
    id: "03",
    number: 3,
    title: "Selecting and Defining Target Behaviors",
    subtitle: "Assessment methods, target selection, operational definitions",
    part: "Part 2 - Selecting, Defining, and Measuring Behavior",
    estMinutes: 80,
    bacb: ["F-1", "F-2", "F-3", "F-4", "H-1", "H-2", "H-3", "H-4"],
  },
  curated: true,
  overview: [
    "Chapter 3 covers how behavior analysts decide which behavior to change and how to define it. Not just any behavior will do: targets must be socially significant, raise habilitation, and meet the relevance of behavior rule (Ayllon & Azrin, 1968). The chapter walks through the five-phase model of behavioral assessment (screening, defining/quantifying, pinpointing, monitoring, follow-up) and the three big assessment methods (indirect, direct, empirical).",
    "It also distinguishes behavioral cusps and pivotal behaviors from ordinary skill targets, explains how to write a function-based vs a topography-based definition, and details how to pick socially validated outcome criteria. The deliverable is a target behavior definition that is objective, clear, complete, and that lets independent observers count the same things.",
  ],
  objectives: [
    { id: "F-1", text: "Review records and available data (educational, medical, historical) at the outset of a case." },
    { id: "F-2", text: "Determine the need for behavior-analytic services." },
    { id: "F-3", text: "Identify and prioritize socially significant behavior-change goals." },
    { id: "F-4", text: "Conduct assessment of relevant skill strengths and deficits." },
    { id: "H-1", text: "State intervention goals in observable and measurable terms." },
    { id: "H-2", text: "Identify potential interventions based on assessment results and the best available scientific evidence." },
    { id: "H-3", text: "Recommend intervention strategies and goals based on client preferences, supporting environments, risks, constraints, and social validity." },
    { id: "H-4", text: "When a target behavior is to be decreased, select an acceptable alternative behavior to be established or increased." },
  ],
  coreConcepts: [
    {
      title: "Role of assessment in ABA",
      body: [
        "Behavioral assessment uses indirect, direct, and empirical methods to identify, define, and determine the function of target behaviors. It is the first phase in the four-phase systematic instruction model (assessment, planning, implementation, evaluation; Stephens, 1976).",
      ],
      bullets: [
        "Phase 1 - Screening: does the behavior warrant intervention?",
        "Phase 2 - Defining and quantifying problems or goals.",
        "Phase 3 - Pinpointing the target behavior(s) to be treated.",
        "Phase 4 - Monitoring progress.",
        "Phase 5 - Following up to confirm maintenance and generalization.",
        "Before assessing, the analyst must have authority, permission, resources, and skill to assess and change the behavior.",
      ],
    },
    {
      title: "Assessment methods (indirect, direct, empirical)",
      body: [
        "Three major assessment method families correspond to three levels of evidence. Indirect methods rely on report; direct methods rely on observation; empirical methods (functional analysis, preference assessment) experimentally test hypotheses.",
      ],
      bullets: [
        "Indirect: interviews, checklists/rating scales, questionnaires, needs-assessment surveys, self-monitoring forms.",
        "Direct: tests and direct observation in the natural environment.",
        "Empirical: functional behavior analysis (FBA) and reinforcer/punisher preference assessments.",
        "ABC (anecdotal) recording yields a temporally sequenced descriptive log of antecedents, behaviors, and consequences as they occur.",
        "Ecological assessment gathers broad context (physiology, environment, history); rarely needed in full but useful when narrower methods stall.",
      ],
    },
    {
      title: "Reactivity and method-quality",
      body: [
        "Reactivity refers to the effects an assessment procedure has on the behavior being assessed. Behavior analysts use unobtrusive measures, repeat observations until reactivity subsides, and account for likely reactive effects when interpreting data. Methods must produce reliable and valid results and be conducted per professional standards.",
      ],
    },
    {
      title: "Social significance and habilitation",
      body: [
        "Target behaviors must be socially significant: they must increase the person's habilitation (adjustment, competence, quality of life), not just satisfy a service provider's preference. Social significance is judged by impact on the person and on the community.",
      ],
      bullets: [
        "Relevance of behavior rule (Ayllon & Azrin, 1968): teach behaviors that will be reinforced in the natural post-intervention environment.",
        "Prerequisite for a useful skill?",
        "Will it grant access to new reinforcing environments?",
        "Will others interact more supportively?",
        "Is it age appropriate?",
        "If reducing behavior, is there an acceptable replacement behavior to teach?",
        "Is the verbal report the target, or the actual behavior of interest?",
      ],
    },
    {
      title: "Behavioral cusps and pivotal behaviors",
      body: [
        "Not all targets are created equal. A behavioral cusp (Rosales-Ruiz & Baer, 1997) is a behavior whose change exposes the person to new environments, reinforcers, contingencies, responses, and stimulus controls - it 'opens the world.' A pivotal behavior produces corresponding modifications or covariations in many other untrained behaviors. Crawling, reading, and generalized imitation are classic cusps; joint attention and self-initiation are classic pivotal behaviors.",
      ],
      bullets: [
        "Cusps differ from component or prerequisite behaviors: cusps occasion new behaviors in novel environments under different stimulus control.",
        "Pivotal behaviors produce widespread untrained behavior change once acquired.",
        "Targeting cusps and pivotal behaviors yields outsized return per teaching unit.",
      ],
    },
    {
      title: "Prioritizing among possible targets",
      body: [
        "When assessment surfaces multiple candidate behaviors, prioritize using a structured rating that weighs danger, frequency, duration of existing problem, potential for reinforcement, future-skill relevance, parent/teacher attention costs, likelihood of success, and cost. Include the client and key family/staff to reduce goal conflicts.",
      ],
    },
    {
      title: "Function-based vs topography-based definitions",
      body: [
        "A function-based definition designates responses as members of the target class solely by their common effect on the environment. A topography-based definition specifies the form or shape of the response. Function-based is preferred when feasible because it captures all topographies that produce the same outcome and excludes look-alikes that do not.",
      ],
      bullets: [
        "Function-based: 'any motor response that knocks the toy off the table.'",
        "Topography-based: 'pushing the toy off the table with the right palm.'",
        "Use topography-based when topography is the target (e.g., a signature, a sound) or when function cannot be observed reliably.",
      ],
    },
    {
      title: "Three criteria for a good target behavior definition",
      body: [
        "A defensible definition must be (1) objective - referring only to observable characteristics and/or observable events; (2) clear - readable and unambiguous so trained observers act on it the same way; and (3) complete - including instances and non-instances so the boundary of the response class is unambiguous (Hawkins & Dobes, 1977).",
      ],
      bullets: [
        "A definition is valid if it captures every aspect the 'complainer' cares about and none other.",
        "Discriminate what IS and what IS NOT an instance of the target behavior.",
        "If observers cannot count the same behavior, refine the definition rather than the observers.",
      ],
    },
    {
      title: "Social validity and outcome criteria",
      body: [
        "A behavior change has social validity if it changes some aspect of the person's life in an important way. Outcome criteria should be set before intervention. Two routes to socially validated performance criteria: (a) assess the performance of highly competent peers, and (b) experimentally vary performance levels to find the optimal one.",
      ],
    },
  ],
  glossary: [
    { term: "Target behavior", definition: "The behavior selected for change in an applied behavior analysis intervention.", sourceHint: "target behavior (Key Terms list)." },
    { term: "Behavioral assessment", definition: "A multi-method process (indirect, direct, empirical) used to identify, define, and determine the function of target behaviors.", sourceHint: "Behavioral assessment involves using indirect, direct, and empirical methods to identify, define, and determine the function of target behaviors." },
    { term: "Anecdotal observation (ABC recording)", definition: "A descriptive, temporally sequenced account of all behaviors of interest and the antecedents and consequences for those behaviors as events occur in the natural environment.", sourceHint: "Anecdotal observation, also called ABC recording, yields a descriptive, temporally sequenced account of all behaviors of interest and the antecedent conditions and consequences for those behaviors." },
    { term: "ABC recording", definition: "Synonym for anecdotal observation: structured note-taking of antecedents, behaviors, and consequences in real time.", sourceHint: "Anecdotal observation, also called ABC recording." },
    { term: "Behavior checklist", definition: "A list of skills or problems used to guide direct observation and indicate possible target behaviors.", sourceHint: "Direct observation with a behavior checklist that contains specific descriptions of various skills can indicate possible target behaviors." },
    { term: "Behavioral cusp", definition: "A behavior whose change exposes the person to new environments, reinforcers, contingencies, responses, and stimulus controls (Rosales-Ruiz & Baer, 1997).", sourceHint: "What makes a behavior change a cusp is that it exposes the individual's repertoire to new environments, especially new reinforcers and punishers, new contingencies, new responses, new stimulus controls." },
    { term: "Pivotal behavior", definition: "A behavior whose acquisition produces corresponding modifications or covariations in many other untrained behaviors.", sourceHint: "Learning a pivotal behavior produces corresponding modifications or covariations in other untrained behaviors." },
    { term: "Ecological assessment", definition: "A broad gathering of information about a person and the environments in which they live and work (physiology, physical environment, social interactions, reinforcement history).", sourceHint: "Ecological assessment entails gathering a large amount of information about the person and the environments in which that person lives and works." },
    { term: "Function-based definition", definition: "A target-behavior definition that designates responses as members of the class solely by their common effect on the environment.", sourceHint: "Function-based definitions designate responses as members of the targeted response class solely by their common effect on the environment." },
    { term: "Topography-based definition", definition: "A target-behavior definition that specifies instances of the class by the shape or form of the response.", sourceHint: "Topography-based definitions define instances of the targeted response class behavior by the shape or form of the behavior." },
    { term: "Habilitation", definition: "The extent to which the person's repertoire maximizes short- and long-term reinforcers for that individual and for others, while minimizing short- and long-term punishers (Hawkins, 1984).", sourceHint: "behaviors that will increase a person's habilitation (adjustment, competence, and quality of life)." },
    { term: "Normalization", definition: "Use of culturally-typical means to enable individuals to attain culturally-typical behaviors and life experiences.", sourceHint: "normalization (Key Terms list)." },
    { term: "Reactivity", definition: "The effects an assessment procedure has on the behavior being assessed.", sourceHint: "Reactivity refers to the effects that an assessment procedure has on the behavior being assessed." },
    { term: "Relevance of behavior rule", definition: "A target behavior should produce reinforcement for the person in the post-intervention environment (Ayllon & Azrin, 1968).", sourceHint: "The relevance of behavior rule requires that a target behavior produce reinforcement for the person in the postintervention environment." },
    { term: "Social validity", definition: "The extent to which the goals, procedures, and outcomes of an intervention are acceptable and important to the consumer (Wolf, 1978).", sourceHint: "A behavior change has social validity if it changes some aspect of the person's life in an important way." },
  ],
  procedures: [
    {
      title: "Five-phase behavioral assessment workflow",
      intro: "Stephens (1976) systematic instruction model adapted for ABA.",
      steps: [
        { title: "Screen", detail: "Decide whether the case warrants behavior-analytic services; review records (medical, educational, historical)." },
        { title: "Define & quantify", detail: "Operationally describe the presenting concerns and gather baseline data using indirect and/or direct methods." },
        { title: "Pinpoint targets", detail: "Select target behaviors using social-significance criteria and a structured prioritization rating." },
        { title: "Monitor", detail: "Collect ongoing data with the chosen measurement system and graphic display; adjust intervention as needed." },
        { title: "Follow up", detail: "Probe maintenance and generalization after intervention ends; document outcomes." },
      ],
    },
    {
      title: "Writing an operational target definition",
      intro: "Use Hawkins & Dobes (1977) criteria of objective, clear, and complete.",
      steps: [
        { title: "Decide function-based vs topography-based", detail: "Pick function-based when feasible; pick topography-based only when topography is the target or function is hard to observe." },
        { title: "List observable features", detail: "Describe what can be seen, heard, or otherwise measured; avoid mentalistic or trait words." },
        { title: "Include positive and negative exemplars", detail: "Specify behaviors that DO count and behaviors that look similar but DO NOT count." },
        { title: "Pilot with two observers", detail: "Have two independent observers score the same sample; if IOA is poor, refine the definition." },
        { title: "Validate against the 'complainer'", detail: "Confirm the definition captures every aspect the referring party cares about and nothing else." },
      ],
    },
    {
      title: "Prioritizing among candidate target behaviors",
      intro: "Use a structured prioritization matrix when assessment surfaces multiple candidates.",
      steps: [
        { title: "List candidates", detail: "Compile each behavior surfaced by assessment with a brief operational description." },
        { title: "Rate each on key dimensions", detail: "Danger, frequency, duration of existence, potential for reinforcement, future-skill relevance, others' burden, likelihood of success, cost." },
        { title: "Score cusp/pivotal status", detail: "Flag candidates that qualify as behavioral cusps or pivotal behaviors; weight them more heavily." },
        { title: "Consult stakeholders", detail: "Run the priority list past the client, parents, teachers, and administrators; reconcile conflicts." },
        { title: "Lock the top targets", detail: "Pick a small number (typically 1-3) and write final operational definitions before intervention design." },
      ],
    },
  ],
  examples: [
    { scenario: "Crawling acquired by an infant.",
      application: "Behavioral cusp: it suddenly opens the world to new contingencies (parents, toys, surfaces) that develop many new behaviors.",
      sourceHint: "Rosales-Ruiz and Baer (1997) cited crawling, reading, and generalized imitation as examples of behavioral cusp behaviors." },
    { scenario: "Teaching a child to push a toy off a table 'in any way' so long as it lands on the floor.",
      application: "Function-based definition: members of the class are defined by the common effect (toy on floor), regardless of which limb pushes it.",
      sourceHint: "Function-based definitions designate responses as members of the targeted response class solely by their common effect on the environment." },
    { scenario: "Teaching a signature exactly as it appears in the bank record.",
      application: "Topography-based definition: the FORM is the target; only the prescribed shape counts as correct.",
      sourceHint: "Topography-based definitions define instances of the targeted response class behavior by the shape or form of the behavior." },
    { scenario: "A teacher sees a parent rate dish-washing as more pressing than language games, even though the analyst believes language is more important.",
      application: "Prioritization is collaborative; targeting the parent's stated need first may indirectly improve later collaboration on the language program (predispose others to interact more supportively).",
      sourceHint: "Will Changing This Behavior Predispose Others to Interact with the Client in a More Appropriate and Supportive Manner?" },
    { scenario: "An observer watches a classroom and notices teacher attention skyrockets the moment a child screams.",
      application: "ABC (anecdotal) recording: a temporally sequenced descriptive log used to generate hypotheses about controlling variables.",
      sourceHint: "Anecdotal observation, also called ABC recording." },
    { scenario: "A new self-monitoring sheet improves attention in the first week, then attention drops back.",
      application: "Reactivity: the assessment procedure itself temporarily affected the behavior. Repeat observation until effects subside.",
      sourceHint: "Reactivity refers to the effects that an assessment procedure has on the behavior being assessed." },
  ],
  comparisons: [
    {
      title: "Function-based vs Topography-based definitions",
      left:  { label: "Function-based", points: [
        "Defines membership by effect on environment.",
        "Captures all topographies that produce the same outcome.",
        "Preferred when feasible; supports generalization.",
      ]},
      right: { label: "Topography-based", points: [
        "Defines membership by physical form.",
        "Needed when topography is the target (e.g., a signature, an articulation).",
        "Useful when function is hard to observe reliably.",
      ]},
    },
    {
      title: "Behavioral cusp vs Pivotal behavior",
      left:  { label: "Behavioral cusp", points: [
        "Opens access to NEW environments and contingencies.",
        "Examples: crawling, reading, generalized imitation.",
        "Judged by extent of behavior changes it enables.",
      ]},
      right: { label: "Pivotal behavior", points: [
        "Produces COVARIATION in many other untrained behaviors.",
        "Examples: joint attention, self-initiation, language motivation.",
        "Judged by spread of untrained gains.",
      ]},
    },
    {
      title: "Indirect vs Direct vs Empirical assessment",
      left:  { label: "Indirect", points: [
        "Interviews, checklists, rating scales, self-monitoring.",
        "Quick; relies on report; lowest evidence weight.",
      ]},
      right: { label: "Direct + Empirical", points: [
        "Direct: in-vivo observation with checklists or measurement.",
        "Empirical: functional analysis and preference assessments.",
        "Higher evidence weight; required for high-stakes decisions.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "If the client cannot articulate what behavior to change, the analyst should pick whatever feels right.",
      reality: "Target selection follows social-significance criteria and stakeholder consultation; the relevance of behavior rule and the prioritization matrix structure the decision.",
      sourceHint: "Prioritization can be accomplished by rating potential target behavior against key questions related to their relative danger, frequency, long-standing existence, potential for reinforcement.",
    },
    {
      myth: "A behavior definition is good if it sounds precise to the writer.",
      reality: "A definition is valid only if independent observers can apply it consistently. Pilot with two observers and refine until IOA is acceptable.",
      sourceHint: "Good target behaviors definitions are necessary for practitioners to collect accurate and believable data to guide ongoing program decisions, apply procedures consistently, and provide accountability.",
    },
    {
      myth: "Reactivity is a methodological flaw to ignore.",
      reality: "Reactivity is expected; design unobtrusive measures, allow the effect to settle, and interpret early data with reactivity in mind.",
      sourceHint: "Behavior analysts should use assessment methods that are as unobtrusive as possible, repeat observations until apparent reactive effects subside.",
    },
    {
      myth: "Teaching a single skill is always less efficient than teaching many at once.",
      reality: "Targeting a behavioral cusp or pivotal behavior often yields more untrained gains than chaining many small targets. Quality of target trumps quantity.",
      sourceHint: "What makes a behavior change a cusp is that it exposes the individual's repertoire to new environments, especially new reinforcers and punishers, new contingencies, new responses, new stimulus controls.",
    },
    {
      myth: "Function-based and topography-based definitions are interchangeable.",
      reality: "They serve different aims. Function-based captures all responses with the same effect; topography-based locks in a particular shape. Picking the wrong one wastes data.",
      sourceHint: "Function-based definitions designate responses as members of the targeted response class solely by their common effect on the environment.",
    },
  ],
  examChecklist: [
    "List the five phases of behavioral assessment in order.",
    "Distinguish indirect, direct, and empirical assessment methods with one example each.",
    "Define the relevance of behavior rule and apply it to a candidate target.",
    "Define behavioral cusp and pivotal behavior with one example each.",
    "List the three criteria (Hawkins & Dobes) of a good target-behavior definition.",
    "Compare function-based and topography-based definitions with one example each.",
    "Explain reactivity and one strategy for managing it.",
    "Describe two methods for setting socially validated outcome criteria.",
    "Apply the prioritization rubric to pick among three candidate target behaviors.",
    "Explain why the client and parents should participate in target selection.",
  ],
  flashcardTerms: [
    "Target behavior", "Behavioral assessment", "ABC recording", "Anecdotal observation",
    "Behavior checklist", "Behavioral cusp", "Pivotal behavior", "Ecological assessment",
    "Function-based definition", "Topography-based definition", "Habilitation",
    "Normalization", "Reactivity", "Relevance of behavior rule", "Social validity",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which type of assessment method includes a functional analysis?",
      options: [
        { label: "A", text: "Indirect" },
        { label: "B", text: "Direct" },
        { label: "C", text: "Empirical" },
        { label: "D", text: "Ecological" },
      ],
      answer: "C",
      explanation: "Functional behavior analyses and preference assessments are EMPIRICAL methods because they test hypotheses experimentally rather than relying on report or simple observation.",
      topic: "Assessment methods",
      sourceHint: "Subsumed under the three major methods for assessment information- indirect, direct, and empirical-are (a) interviews, checklists/rating scales, (b) tests and direct observations, and (c) functional behavior analysis and reinforcer/punishment preference assessment methods, respectively.",
    },
    {
      id: "Q2",
      question: "An infant begins to crawl. Rosales-Ruiz and Baer (1997) would classify crawling primarily as a:",
      options: [
        { label: "A", text: "Pivotal behavior" },
        { label: "B", text: "Behavioral cusp" },
        { label: "C", text: "Component behavior" },
        { label: "D", text: "Topography-based response class" },
      ],
      answer: "B",
      explanation: "Crawling is a classic cusp because it suddenly exposes the child to new environments, reinforcers, and contingencies that develop many subsequent behaviors.",
      topic: "Cusp vs pivotal",
      sourceHint: "Rosales-Ruiz and Baer (1997) cited crawling, reading, and generalized imitation as examples of behavioral cusp behaviors.",
    },
    {
      id: "Q3",
      question: "A definition states 'pushing the cup with the right palm so it crosses the table edge.' This is best described as:",
      options: [
        { label: "A", text: "Function-based" },
        { label: "B", text: "Topography-based" },
        { label: "C", text: "Ecological" },
        { label: "D", text: "Anecdotal" },
      ],
      answer: "B",
      explanation: "The definition specifies the SHAPE of the response (right palm) rather than membership by effect; it is topography-based.",
      topic: "Definitions",
      sourceHint: "Topography-based definitions define instances of the targeted response class behavior by the shape or form of the behavior.",
    },
    {
      id: "Q4",
      question: "Which rule says a target behavior should produce reinforcement for the person in the post-intervention environment?",
      options: [
        { label: "A", text: "Relevance of behavior rule" },
        { label: "B", text: "Normalization principle" },
        { label: "C", text: "Cusp principle" },
        { label: "D", text: "Dead-man test" },
      ],
      answer: "A",
      explanation: "The relevance of behavior rule (Ayllon & Azrin, 1968) requires that the target behavior contact reinforcement in the natural setting once intervention ends.",
      topic: "Social significance",
      sourceHint: "The relevance of behavior rule requires that a target behavior produce reinforcement for the person in the postintervention environment.",
    },
    {
      id: "Q5",
      question: "After 3 days of using a new wristband self-monitoring counter, the target behavior decreases sharply, then drifts back up. Best explanation:",
      options: [
        { label: "A", text: "Treatment integrity failed." },
        { label: "B", text: "The wristband is a punisher." },
        { label: "C", text: "Reactivity to the assessment procedure." },
        { label: "D", text: "Spontaneous recovery." },
      ],
      answer: "C",
      explanation: "Reactivity is the temporary effect of the assessment procedure itself; repeat observations until the effect subsides before drawing conclusions.",
      topic: "Reactivity",
      sourceHint: "Reactivity refers to the effects that an assessment procedure has on the behavior being assessed.",
    },
    {
      id: "Q6",
      question: "Which of the following is NOT one of Hawkins & Dobes' (1977) criteria for a good target-behavior definition?",
      options: [
        { label: "A", text: "Objective" },
        { label: "B", text: "Clear" },
        { label: "C", text: "Complete" },
        { label: "D", text: "Brief" },
      ],
      answer: "D",
      explanation: "The three criteria are objective, clear, and complete. Brevity is desirable but not a stated criterion.",
      topic: "Definitions",
      sourceHint: "A good definition must be objective, clear, and complete, and must discriminate between what is and what is not an instance of the target behavior.",
    },
    {
      id: "Q7",
      question: "A behavior change has social validity when:",
      options: [
        { label: "A", text: "The IOA is above 80%." },
        { label: "B", text: "The trend line is statistically significant." },
        { label: "C", text: "It changes the person's life in an important way." },
        { label: "D", text: "It generalizes to one new setting." },
      ],
      answer: "C",
      explanation: "Social validity refers to the importance of the change to the consumer and their community (Wolf, 1978).",
      topic: "Social validity",
      sourceHint: "A behavior change has social validity if it changes some aspect of the person's life in an important way.",
    },
  ],
  reviewPrompts: [
    "Walk through the five-phase behavioral assessment workflow on a hypothetical case.",
    "Pick a target behavior and write both a function-based and a topography-based definition.",
    "Identify one cusp and one pivotal behavior in a child you know and explain the difference.",
    "Apply the relevance of behavior rule to two candidate targets and pick the better one.",
    "Plan two methods to set socially validated outcome criteria for a sample target.",
    "Describe one likely source of reactivity in your current data collection and how you would mitigate it.",
  ],
  recap: [
    "Selecting and defining target behaviors is the first leverage point in any ABA program. Behavioral assessment uses indirect, direct, and empirical methods in a five-phase workflow (screen, define, pinpoint, monitor, follow up).",
    "Targets must be socially significant: they must improve habilitation, satisfy the relevance of behavior rule, and ideally be behavioral cusps or pivotal behaviors that unlock many other gains.",
    "A defensible target-behavior definition is objective, clear, and complete; function-based when the effect is the target, topography-based when the shape is. Outcome criteria and social validity are set before intervention, not after.",
  ],
  sourceExcerpts: [
    {
      heading: "Behavioral assessment definition",
      text: "Behavioral assessment involves using indirect, direct, and empirical methods to identify, define, and determine the function of target behaviors. Behavioral assessment consists of five phases or functions: (a) screening, (b) defining and quantifying problems or goals, (c) pinpointing the target behavior(s) to be treated, (d) monitoring progress, and (e) following up.",
    },
    {
      heading: "Behavioral cusp (Rosales-Ruiz & Baer, 1997)",
      text: "A behavior that has consequences beyond the change itself, some of which may be considered important... What makes a behavior change a cusp is that it exposes the individual's repertoire to new environments, especially new reinforcers and punishers, new contingencies, new responses, new stimulus controls, and new communities of maintaining or destructive contingencies.",
    },
    {
      heading: "Function-based vs topography-based",
      text: "Function-based definitions designate responses as members of the targeted response class solely by their common effect on the environment. Topography-based definitions define instances of the targeted response class behavior by the shape or form of the behavior.",
    },
    {
      heading: "Target definition criteria",
      text: "A good definition must be objective, clear, and complete, and must discriminate between what is and what is not an instance of the target behavior. A target behavior definition is valid if it enables observers to capture every aspect of the behavior that the 'complainer' is concerned with and none other.",
    },
  ],
};

export default chapter03Module;
